<template>
  <section class="invoice-preview-wrapper">
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col md="12">
            <h2>{{ $t("reception.patient-lobby-list") }}</h2>
          </b-col>
          <b-col cols="12" md="12">
            <br />
          </b-col>
          <b-col md="6" cols="12">
            <AutoComplete
              :label="$t('reception.patient-name')"
              :placeholder="$t('reception.search-patient')"
              endpoint="patient"
              text-title="name"
              :serializer="(item) => item"
              :value="selectedPatient"
              @input="handlePatientSelect"
            />
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              class="mt-50 mr-1 d-flex align-items-center justify-content-center"
              variant="primary"
              @click="getPatientCards()"
            >
              {{ $t("common.search") }}
              <b-icon-search class="ml-1"></b-icon-search>
            </b-button>
            
            <!-- Filter Radio Buttons -->
            <b-button-group class="mt-50">
              <b-button
                :variant="activeFilter === null ? 'primary' : 'outline-secondary'"
                @click="setActiveFilter(null)"
              >
                {{ $t("common.all") }}
              </b-button>
              <b-button
                :variant="activeFilter === 1 ? 'success' : 'outline-success'"
                @click="setActiveFilter(1)"
              >
                {{ $t("common.active") }}
              </b-button>
              <b-button
                :variant="activeFilter === 0 ? 'danger' : 'outline-danger'"
                @click="setActiveFilter(0)"
              >
                {{ $t("common.discharged") }}
              </b-button>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col>
            <b-table
              :items="patientCardsData"
              responsive
              :fields="tableColumns"
              primary-key="id"
              class="position-relative"
              show-empty
              empty-text="No matching patients found"
              v-if="!loading"
            >
                            <!-- Patient Name with Number and Status -->
              <template #cell(patient_info)="data">
                <div class="d-flex flex-column position-relative">
                  <!-- Status Badge in top right corner -->
                  <b-badge
                    :variant="data.item.is_active ? 'success' : 'danger'"
                    class="position-absolute"
                    style="top: 0; right: 0; font-size: 0.75rem; z-index: 1;"
                  >
                    {{
                      data.item.is_active
                        ? $t("common.active")
                        : $t("common.discharged")
                    }}
                  </b-badge>
                  
                  <br>
                   
                  <span class="font-weight-bold text-primary h6 mb-2 pr-4 mt-1">
                    {{ getNestedProperty(data.item, "patient.name") }}
                  </span>
                  <div class="d-flex flex-column">
                    <small class="text-nowrap">
                      {{ $t("reception.card-type") }}:
                      <b-badge variant="light-info" class="ml-1">
                        {{ data.item.type || "N/A" }}
                      </b-badge>
                    </small>
                    <small class=" ">
                      {{ $t("reception.patient-no") }}:
                      <span class="font-weight-semibold text-dark">
                        {{ getNestedProperty(data.item, "patient.patient_no") }}
                      </span>
                    </small>
                    <!-- Room info - only show if patient is active -->
                    <small v-if="data.item.is_active && getCurrentRoom(data.item) && getCurrentRoom(data.item).room" class=" ">
                      {{ $t("reception.room") }}:
                      <b-badge 
                        :variant="getCurrentRoom(data.item).room.type === 'vip' ? 'light-warning' : 'light-info'" 
                        class="ml-1"
                        style="font-size: 0.7rem;"
                      >
                        {{ getCurrentRoom(data.item).room.name }}
                      </b-badge>
                    </small>
                  </div>
                </div>
              </template>

              <!-- Patient Demographics -->
              <template #cell(demographics)="data">
                <div class="d-flex flex-column">
                  <span>
                    <strong>{{ $t("reception.gender") }}:</strong>
                    {{ getNestedProperty(data.item, "patient.gender") }}
                  </span>
                  <span>
                    <strong>{{ $t("reception.birth-date") }}:</strong>
                    {{
                      formatDate(
                        getNestedProperty(data.item, "patient.birth_date", null)
                      )
                    }}
                  </span>
                  <span>
                    <strong>{{ $t("reception.city") }}:</strong>
                    {{ getNestedProperty(data.item, "patient.city_name") }}
                  </span>
                </div>
              </template>

              <!-- Contact Information -->
              <template #cell(contact)="data">
                <div class="d-flex flex-column">
                  <span
                    class="text-nowrap"
                    :style="{
                      direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr',
                    }"
                  >
                    <strong>{{ $t("reception.phone") }}:</strong>
                    {{ getNestedProperty(data.item, "patient.phone") }}
                  </span>
                  <span
                    v-if="
                      getNestedProperty(data.item, 'patient.national_id', null)
                    "
                  >
                    <strong>{{ $t("reception.national-id") }}:</strong>
                    {{ getNestedProperty(data.item, "patient.national_id") }}
                  </span>
                </div>
              </template>

              <!-- Admission Information -->
              <template #cell(admission)="data">
                <div class="d-flex flex-column">
                  <span>
                    <strong>{{ $t("reception.admitted") }}:</strong>
                    {{ formatDateTime(data.item.created_at) }}
                  </span>
                  <span v-if="data.item.out_date_time" class="text-warning">
                    <strong>{{ $t("reception.discharged") }}:</strong>
                    {{ formatDateTime(data.item.out_date_time) }}
                  </span>
                  <span
                    v-if="
                      getNestedProperty(
                        data.item,
                        'patient.number_of_total_stays_in_hospital',
                        null
                      )
                    "
                  >
                    <strong>{{ $t("reception.total-stays") }}:</strong>
                    {{
                      getNestedProperty(
                        data.item,
                        "patient.number_of_total_stays_in_hospital"
                      )
                    }}
                  </span>
                </div>
              </template>

              <!-- Actions -->
              <template #cell(actions)="data">
                <div class="text-nowrap d-flex flex-column">
                  <b-button
                    size="sm"
                    pill
                    variant="outline-secondary"
                    class="mb-1"
                    @click="
                      $router.push({
                        name: 'active-patient-card',
                        params: { 
                          id: data.item.patient.id, 
                          is_active: data.item.is_active, 
                          card_id: data.item.id
                        },
                      })
                    "
                  >
                    {{ $t("common.preview") }}
                  </b-button>
                  <b-button
                     size="sm"
                    pill
                    variant="outline-primary"
                    class="mb-1"
                    @click="
                      $router.push({
                        name: 'vital-signs',
                        params: {
                          id: data.item.id,
                          name: data.item.patient.name,
                          is_active: data.item.is_active, 
                         },
                      })
                    "
                  >
                    {{ $t("common.vitals-and-drugs") }}
                  </b-button>
                  <b-button
                    size="sm"
                    pill
                    variant="outline-info"
                    class="mb-1"
                    @click="
                      $router.push({
                        name: 'doctor-notes-list',
                        params: {
                          id: data.item.id,
                          name: data.item.patient.name,
                          is_active: data.item.is_active, 
                         },
                      })
                    "
                  >
                    {{ $t("reception.doctor-notes") }}
                  </b-button>
                  <b-button
                     size="sm"
                    pill
                    variant="outline-success"
                    class="mb-1"
                    @click="
                      $router.push({
                        name: 'follow-up-list',
                        params: {
                          id: data.item.id,
                          name: data.item.patient.name,
                          is_active: data.item.is_active, 
                         },
                      })
                    "
                  >
                    {{ $t("office.follow-up") }}
                  </b-button>
                </div>
              </template>
            </b-table>
            <template v-if="loading">
              <div>
                <div class="d-flex justify-content-center mb-1">
                  <b-spinner label="Loading..." />
                </div>
              </div>
            </template>
          </b-col>
        </b-row>
      </div>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t("common.showing") }} 1 {{ $t("common.to") }}
              {{ Math.min(take, dataCount) }} {{ $t("common.of") }}
              {{ dataCount }} {{ $t("common.results") }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="handlePageChange"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BButtonGroup,
  BBadge,
  BPagination,
  BSpinner,
  BIconSearch,
} from "bootstrap-vue";
import { getNestedProperty } from "@/utils/nestedProperty";
import AutoComplete from "@/components/common/AutoComplete.vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BCard,
    BButton,
    BButtonGroup,
    BBadge,
    BPagination,
    BSpinner,
    BIconSearch,
    AutoComplete,
    FeatherIcon,
  },
  data() {
    return {
      tableColumns: [
        {
          key: "patient_info",
          label: this.$t("reception.patient-info"),
          sortable: false,
          thStyle: { width: "200px" },
        },

        {
          key: "demographics",
          label: this.$t("reception.demographics"),
          sortable: false,
          thStyle: { width: "200px" },
        },
        {
          key: "contact",
          label: this.$t("reception.contact"),
          sortable: false,
          thStyle: { width: "180px" },
        },

        {
          key: "admission",
          label: this.$t("reception.admission-info"),
          sortable: false,
          thStyle: { width: "200px" },
        },
        {
          key: "actions",
          label: this.$t("common.actions"),
          thStyle: { width: "200px" },
        },
      ],
      patientCardsData: [],
      selectedPatient: null,
      page: 1,
      dataCount: 0,
      take: 10,
      loading: false,
      activeFilter: 1,
    };
  },

  mounted() {
    this.getPatientCards();
  },
  methods: {
    getNestedProperty,

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";
      return new Date(dateTime).toLocaleString();
    },

    getCurrentRoom(patientCard) {
      // Get patient room data and select the last (most recent) one
      const patientRooms = getNestedProperty(patientCard, 'patient_room', []);
      if (!patientRooms || patientRooms.length === 0) {
        return null;
      }
      
      // If it's an array, get the last one (most recent)
      if (Array.isArray(patientRooms)) {
        return patientRooms[patientRooms.length - 1];
      }
      
      // If it's a single object, return it
      return patientRooms;
    },

    handlePatientSelect(patient) {
      this.selectedPatient = patient;
      this.page = 1; // Reset to first page when searching
      this.getPatientCards();
    },

    handlePageChange(newPage) {
      this.page = newPage;
      this.getPatientCards();
    },

    async getPatientCards() {
      this.loading = true;
      
      // Use only important includes to optimize the API call
      const importantIncludes = [
        'patient',
        'patientRoom',
        'doctorNotes',
        'vitalSigns',
        'stays'
      ];
      
      let url = `/patientCard?include=${importantIncludes.join(',')}&page=${this.page}&take=${this.take}&sort=-id`;

      // Filter by selected patient if one is chosen
      if (this.selectedPatient && this.selectedPatient.id) {
        url += `&filter[patient_id]=${this.selectedPatient.id}`;
      }

      // Add filter for active/inactive patients
      if (this.activeFilter !== null) {
        url += `&filter[is_active]=${this.activeFilter}`;
      }

      // Filter to only show patients with card type "IN"
      url += '&filter[type]=IN'

      await this.getList(url);
      for (let item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }
      this.dataCount = this.all_data.total;
      this.patientCardsData = this.all_data.data;
      this.loading = false;
    },

    setActiveFilter(value) {
      this.activeFilter = value;
      this.page = 1; // Reset to first page when changing filter
      this.getPatientCards();
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}

#modal-add___BV_modal_content_ {
  width: 100% !important;
}

#modal-add {
  margin-left: -8% !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
